import React, { useContext } from 'react';
import { Typography } from 'antd';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import {
  calculateAnnualSavings,
  calculateOptimizeROIPercent,
  calculatePaybackMonths,
  calculateTotalSavings,
  formatPaybackPeriod,
} from '@totem/components/ticketing/ticket/specialCases/utilities';
import { isNotNull } from '@totem/utilities/common';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Financial table
// ---------------------------------------------------------------------------

type FinancialRow = {
  label: string;
  value: React.ReactNode;
  note?: string;
  highlight?: boolean;
};

const FinancialTable = ({ rows }: { rows: FinancialRow[] }) => (
  <div
    style={{
      border: '1px solid #E5E7EB',
      borderRadius: 8,
      overflow: 'hidden',
    }}
  >
    {rows.map((row, idx) => (
      <div
        key={idx}
        style={{
          display: 'grid',
          gridTemplateColumns: '220px 160px 1fr',
          alignItems: 'center',
          padding: '12px 16px',
          background: row.highlight
            ? '#F0FDF4'
            : idx % 2 === 0
              ? '#FFFFFF'
              : '#FAFAFA',
          borderBottom: idx < rows.length - 1 ? '1px solid #E5E7EB' : 'none',
        }}
      >
        <div style={{ fontSize: 13, color: '#374151' }}>{row.label}</div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: row.highlight ? '#16A34A' : '#111827',
          }}
        >
          {row.value}
        </div>
        <div style={{ fontSize: 12, color: '#9CA3AF' }}>{row.note ?? ''}</div>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Execution timeline
// ---------------------------------------------------------------------------

type TimelineStepDef = {
  title: string;
  duration?: string;
  description: string;
  role: string;
  active?: boolean;
  complete?: boolean;
};

const TimelineStep = ({
  step,
  isLast,
}: {
  step: TimelineStepDef;
  isLast: boolean;
}) => (
  <div style={{ display: 'flex', gap: 16, paddingBottom: isLast ? 0 : 24 }}>
    {/* Circle + line */}
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: step.active
            ? '#E05A2B'
            : step.complete
              ? '#16A34A'
              : 'transparent',
          border: `2px solid ${step.active ? '#E05A2B' : step.complete ? '#16A34A' : '#D1D5DB'}`,
          flexShrink: 0,
          marginTop: 2,
        }}
      />
      {!isLast && (
        <div
          style={{
            width: 2,
            flex: 1,
            background: '#E5E7EB',
            marginTop: 4,
            minHeight: 20,
          }}
        />
      )}
    </div>

    {/* Content */}
    <div style={{ flex: 1, paddingBottom: isLast ? 0 : 4 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 4,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
          {step.title}
        </span>
        {step.duration && (
          <span
            style={{
              fontSize: 11,
              color: '#9CA3AF',
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
              marginLeft: 12,
            }}
          >
            {step.duration}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 13,
          color: '#6B7280',
          marginBottom: 4,
          lineHeight: 1.5,
        }}
      >
        {step.description}
      </div>
      <div style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic' }}>
        {step.role}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Status → step active/complete mapping
// ---------------------------------------------------------------------------

const STATUS_STEP_INDEX: Record<string, number> = {
  Draft: 0,
  'Pending Review': 0,
  Approved: 1,
  'In Progress': 1,
  Verifying: 2,
  'Savings Booked': 3,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const OpportunityBriefTab = () => {
  const { ticket } = useContext(TicketContext);

  if (!ticket?.ticket) return null;

  const { ticket: tkData } = ticket;
  const opt = tkData.extras?.optimize ?? null;

  const annualSavings = isNotNull(opt) ? calculateAnnualSavings(opt) : 0;
  const fiveYearTotal = isNotNull(opt) ? calculateTotalSavings(opt) : 0;
  const roiPercent = isNotNull(opt) ? calculateOptimizeROIPercent(opt) : null;
  const paybackMonths = isNotNull(opt) ? calculatePaybackMonths(opt) : null;

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val);

  const financialRows: FinancialRow[] = [
    {
      label: 'Required investment',
      value:
        isNotNull(opt) && opt.requiredInvestment === 0
          ? '$0'
          : isNotNull(opt)
            ? formatCurrency(opt.requiredInvestment)
            : '—',
      note:
        isNotNull(opt) && opt.requiredInvestment === 0
          ? 'No capital, parts, or new equipment'
          : 'One-time investment required',
    },
    {
      label: 'Annual savings',
      value: `+${formatCurrency(annualSavings)}`,
      note:
        isNotNull(opt) &&
        (opt.savingsTermPeriod === 'Year' || opt.savingsTermPeriod === 'Years')
          ? `Recurring for ${opt.savingsTerm} year term`
          : isNotNull(opt)
            ? `Over ${opt.savingsTerm} month term`
            : '',
    },
    {
      label: 'Service term',
      value: isNotNull(opt)
        ? `${opt.savingsTerm} ${opt.savingsTermPeriod.toLowerCase()}`
        : '—',
      note: 'Savings recur for full term',
    },
    {
      label: isNotNull(opt)
        ? `${opt.savingsTerm}-${opt.savingsTermPeriod === 'Year' || opt.savingsTermPeriod === 'Years' ? 'year' : 'month'} total`
        : '5-year total',
      value: `+${formatCurrency(fiveYearTotal)}`,
      note: 'Net savings over full term',
      highlight: true,
    },
    {
      label: 'ROI',
      value:
        isNotNull(roiPercent) && !Number.isNaN(roiPercent)
          ? `${roiPercent.toLocaleString('en-US', { maximumFractionDigits: 0 })}%`
          : '—',
      note:
        isNotNull(opt) && opt.requiredInvestment === 0
          ? 'Of total project cost'
          : 'Return on investment',
    },
    {
      label: 'Payback period',
      value: (
        <em style={{ fontStyle: 'italic' }}>
          {formatPaybackPeriod(paybackMonths)}
        </em>
      ),
      note:
        paybackMonths === null
          ? 'Zero-capital opportunity'
          : `Breakeven in ${formatPaybackPeriod(paybackMonths)}`,
    },
  ];

  // Execution timeline steps
  const opportunityStatus = opt?.opportunityStatus ?? '';
  const currentStepIdx = STATUS_STEP_INDEX[opportunityStatus] ?? 0;

  const steps: TimelineStepDef[] = [
    {
      title: 'Customer approval',
      duration: '5 min',
      description:
        'Review the financial summary and approve the work order. No site visit required.',
      role: 'You (Asset Manager)',
      active: currentStepIdx === 0,
      complete: currentStepIdx > 0,
    },
    {
      title: 'Implementation',
      duration: isNotNull(opt) ? `${opt.levelOfEffort} effort` : undefined,
      description:
        isNotNull(opt) && opt.stepsRequired
          ? opt.stepsRequired
          : 'Scheduled work carried out by the assigned IB engineer.',
      role: 'IB Optimization Engineer',
      active: currentStepIdx === 1,
      complete: currentStepIdx > 1,
    },
    {
      title: 'Verification window',
      duration: '30 days',
      description:
        'Savings are measured against baseline bills to confirm the expected reduction has been achieved.',
      role: 'IB · automated',
      active: currentStepIdx === 2,
      complete: currentStepIdx > 2,
    },
    {
      title: 'Savings realized',
      duration: 'Month 1',
      description:
        'Verified savings are recorded and the opportunity is closed. Savings recur for the full service term.',
      role: 'Auto',
      active: currentStepIdx === 3,
      complete: currentStepIdx > 3,
    },
  ];

  return (
    <div style={{ maxWidth: 680 }}>
      {/* Headline */}
      {tkData.description && (
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: '#111827',
            lineHeight: 1.35,
            marginBottom: 28,
          }}
        >
          {tkData.description}
        </div>
      )}

      {/* Why this matters */}
      {isNotNull(opt) && opt.savingsType && (
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: '#111827',
              marginBottom: 6,
            }}
          >
            Why this matters to you
          </div>
          <Text style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
            {opt.savingsType} opportunity with{' '}
            <strong>{opt.levelOfEffort?.toLowerCase()} effort</strong> and{' '}
            <strong>{opt.scopeOfReturn?.toLowerCase()} return</strong>. This
            opportunity requires{' '}
            {opt.requiredInvestment === 0
              ? 'no capital investment'
              : `a ${formatCurrency(opt.requiredInvestment)} investment`}{' '}
            and delivers {formatCurrency(annualSavings)} in annual savings over
            a {opt.savingsTerm}-{opt.savingsTermPeriod?.toLowerCase() ?? 'year'}{' '}
            term.
          </Text>
        </div>
      )}

      {/* Financial breakdown */}
      <div style={{ marginBottom: 32 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#111827',
            marginBottom: 12,
          }}
        >
          Financial breakdown
        </div>
        <FinancialTable rows={financialRows} />
      </div>

      {/* Execution timeline */}
      <div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#111827',
            marginBottom: 16,
          }}
        >
          Execution timeline
        </div>
        {steps.map((step, idx) => (
          <TimelineStep
            key={step.title}
            step={step}
            isLast={idx === steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export default OpportunityBriefTab;
